import { Injectable } from "@angular/core";
import { NavigationStart, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { LocalStorageService } from "./local-storage.service";

const FILTER_STORAGE_PREFIX = "route-filter-state::";

@Injectable({
  providedIn: "root"
})
export class RouteFilterStorageService {
  private currentRouteScope: string;

  constructor(private router: Router,
              private localStorageService: LocalStorageService) {
    this.currentRouteScope = this.normalizeRoute(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        const nextRoutePath = this.normalizeRoute(event.url);
        if (this.areRoutesRelated(this.currentRouteScope, nextRoutePath)) {
          return;
        }

        this.clearRouteScope(this.currentRouteScope);
        this.currentRouteScope = nextRoutePath;
      });
  }

  get<T>(scope: string): T | null {
    return this.localStorageService.getItem<T>(this.buildScopedKey(scope));
  }

  set<T>(scope: string, value: T): void {
    this.localStorageService.setItem(this.buildScopedKey(scope), value);
  }

  remove(scope: string): void {
    this.localStorageService.removeItem(this.buildScopedKey(scope));
  }

  private buildScopedKey(scope: string): string {
    return `${FILTER_STORAGE_PREFIX}${this.currentRouteScope}::${scope}`;
  }

  private areRoutesRelated(activeScope: string, nextRoutePath: string): boolean {
    if (activeScope === nextRoutePath) {
      return true;
    }

    if (nextRoutePath.startsWith(`${activeScope}/`)) {
      return true;
    }

    return activeScope.startsWith(`${nextRoutePath}/`);
  }

  private clearRouteScope(routePath: string): void {
    if (!routePath) {
      return;
    }

    const routePrefix = `${FILTER_STORAGE_PREFIX}${routePath}::`;
    const keysToRemove: string[] = [];

    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (key?.startsWith(routePrefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => this.localStorageService.removeItem(key));
  }

  private normalizeRoute(url: string): string {
    const routeWithoutQueryAndHash = url.split(/[?#]/)[0] || "/";
    if (routeWithoutQueryAndHash === "/") {
      return routeWithoutQueryAndHash;
    }

    return routeWithoutQueryAndHash.endsWith("/")
      ? routeWithoutQueryAndHash.slice(0, -1)
      : routeWithoutQueryAndHash;
  }
}
